import React, { useContext, useEffect, useState } from 'react';
import { To, createSearchParams, useNavigate } from 'react-router-dom';
import hmacSHA384 from 'crypto-js/hmac-sha384';
import {
	IOfferContext,
	OfferContext,
	PayOneService,
	ButtonState,
	checkValidation,
	checkPayOneStatus,
	VignetteContext,
	IVignetteContext,
	getPreauthOfferHash,
	Variants,
	getUrlRedirectPath,
	completeOrder,
	Countries,
	ICustomer,
	setRouteLangParam,
	IAdditionalInformation,
	getLabelValue,
	OrderService,
	ITypeCreditCardDataFields,
	getUrlBasePath,
} from '@adac-mp/shared';
import { Modal, Input, Typography, Button, Drawer, Info, MastercardIcon, VisaIcon, Richtext } from '../../index';
import { useValidatedStates } from '../../hooks/useValidatedStates';

import ccBack from '../../assets/Cc-back.jpg';
import { gatherProductTrackingData } from '../../utils/tracking/tracking';
import { renderErrorMessage } from '../../utils';

type PayOneRequest = {
	request: string;
	aid: string; // your AID
	portalid: string; // your PortalId
	mid: string; // your MID
	mode: string; //'live', // desired mode
	responsetype: string; // fixed value
	encoding: string; // fixed value
	storecarddata: string; // fixed value
	hash?: string;
};

interface IPaymentModalProps {
	labels?: ITypeCreditCardDataFields;
	showModal: boolean;
	paymentUpdate?: boolean;
	variant: Variants;
	customer?: ICustomer;
	countryUrl?: Countries;
	additionalData?: IAdditionalInformation;
	onClose: (bool: boolean) => void;
	onOrderCompleted: (pathname: To) => void;
	onRedirect: (redirectUrl: string) => void;
}

const generateHash = (array: string[]) => {
	return hmacSHA384(array.join(''), process.env.PAYONE_SECTRETS_KEY).toString().toLowerCase();
};

export const PaymentModal = ({
	labels = undefined,
	showModal,
	paymentUpdate = false,
	variant,
	countryUrl,
	customer,
	additionalData,
	onClose,
	onOrderCompleted,
	onRedirect,
}: IPaymentModalProps) => {
	const navigate = useNavigate();
	const { labels: currentLables, lang, updateOrderInfo } = useContext<IOfferContext>(OfferContext);
	const { cartItems } = useContext<IVignetteContext>(VignetteContext);
	const [initialized, setInitialized] = useState<boolean>(false);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [buttonState, setButtonState] = useState<ButtonState>('active');
	const stateInit = {
		cardHolder: {
			value:
				customer?.person?.firstName && customer.person.lastName
					? `${customer?.person?.firstName} ${customer?.person?.lastName}`
					: '',
			error: false,
			warning: false,
			required: true,
		},
	};
	const [state, { updateWithValidation, updateWithoutError, hasErrors }] = useValidatedStates(stateInit);

	useEffect(() => {
		updateWithoutError(
			'cardHolder',
			customer?.person?.firstName && customer.person.lastName
				? `${customer?.person?.firstName} ${customer?.person?.lastName}`
				: '',
		);
	}, [customer?.person?.firstName, customer?.person?.lastName]);

	useEffect(() => {
		if (showModal) {
			const offers = cartItems.map((item) => item.offer);
			gatherProductTrackingData({ offers, status: 'checkout', additionalData: { checkOutStep: 2 } });
		}
	}, [showModal]);

	const getConfig = () => {
		const styleInput =
			"border-radius: 8px;font-size: 18px; border: 1px solid #EBEBEB; background: white; color: #121212;font-family: 'Milo Pro Bold', sans-serif; padding: 16px; width: 100%; outline: none;";
		const styleSelect = `${styleInput} appearance: none;`;
		return {
			fields: {
				cardpan: {
					selector: 'card_pan',
					style: styleInput,
					styleFocus: styleInput,
					type: 'input',
					iframe: {
						height: '58px',
						width: '100%',
					},
				},
				cardcvc2: {
					selector: 'card_cvc2',
					style: styleInput,
					styleFocus: styleInput,
					type: 'text', // Could be "text" or "password".
					size: '4',
					// This is a configuration example for something like:
					// * Visa: Exactly 3 digits.
					// * JCB: No CVC, input disabled.
					// * Mastercard: Not configured, up to maxlength digits or none.
					maxlength: '4',
					iframe: {
						height: '58px',
						width: '100%',
					},
				},
				cardexpiremonth: {
					selector: 'card_expire_month',
					style: styleSelect,
					styleFocus: styleSelect,
					type: 'select', // select (default), text, tel (for alternative keyboards on mobile devices),
					iframe: {
						height: '58px',
						width: '100%',
					},
				},
				cardexpireyear: {
					selector: 'card_expire_year',
					style: styleSelect,
					styleFocus: styleSelect,
					type: 'select', // select (default), text, tel (for alternative keyboards on mobile devices)
					iframe: {
						height: '58px',
						width: '100%',
					},
				},
			},
			autoCardtypeDetection: {
				supportedCardtypes: ['V', 'M'],
			},
			events: {
				// Javascript callback function will be triggered once “hosted iFrames” are completely loaded
				rendered: () => {
					setInitialized(true);
				},
			},
		};
	};

	const initialize = () => {
		const request: PayOneRequest = {
			request: 'creditcardcheck',
			aid: process.env.PAYONE_SUBACCOUNT_ID || '', // your AID
			portalid: process.env.PAYONE_PORTAL_ID || '', // your PortalId
			mid: process.env.PAYONE_MERCHANT_ID || '', // your MID
			mode: process.env.MODE || '', //'live', // desired mode
			// All these fixed values get used in the backend code too, to precalculate the hash.
			// So that the credit card payment doesn't need an extra API call to our backend, which
			// might be unavailable while deployments are happening.
			responsetype: 'JSON', // fixed value
			encoding: 'UTF-8', // fixed value
			storecarddata: 'yes', // fixed value
		};

		const obj = Object.keys(request).sort((a, b) => {
			return a[0].toLowerCase().localeCompare(b[0].toLowerCase());
		});

		const hashValues = obj.map((key) => request[key]);
		request.hash = generateHash(hashValues);

		// Have to load the payone script first.
		window.iframes = new window.Payone.ClientApi.HostedIFrames(getConfig(), request);
	};

	const closeModal = () => {
		setInitialized(false);
		onClose(false);
	};

	const payonePreAuth = (customerHash: string, creditCardCheckResponse) => {
		const isDKVPage =
			window.location.pathname.includes('camper-mautbox') || window.location.search.includes('camper-mautbox');
		const cartHash = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken');
		const lastName = state.cardHolder.value.split(' ').pop();
		const isPreviewEnvironment = process.env.VERCEL_ENV !== 'production';
		const domain = getUrlRedirectPath();
		const preAuth = {
			offerHash: getPreauthOfferHash(variant === 'dkv'),
			customerHash,
			cartHash,
			pseudoCardPan: creditCardCheckResponse.pseudocardpan,
			successUrl: `${domain}/processing/${variant}${setRouteLangParam(lang)}${
				isPreviewEnvironment ? '&featureAppPreview=true' : ''
			}`,
			errorUrl: `${domain}/error${setRouteLangParam(lang)}${
				isPreviewEnvironment ? '?featureAppPreview=true' : ''
			}`,
			backUrl: `${domain}/${isDKVPage ? 'camper-mautbox' : `${countryUrl}`}/checkout${setRouteLangParam(lang)}${
				isPreviewEnvironment ? '?featureAppPreview=true' : ''
			}`,
			lastName,
		};
		PayOneService.preauthorization(preAuth, token, lang).then((res) => {
			const { offerHash, status, redirectUrl, payoneError } = res;
			if (offerHash) {
				sessionStorage.setItem('offerHash', offerHash);
			}
			if (status === 'APPROVED' && customerHash && cartHash) {
				checkPayOneStatus(offerHash, cartHash, token, 3000, lang)
					.then((result) => {
						if (result) {
							completeOrder({
								lang,
								offerHash: result.offerHash,
								additionalData,
								variant,
								errorLabel: getLabelValue('mp.generalError', currentLables),
								updateOrderInfo,
								onOrderCompleted,
								gatherProductTrackingData,
								setButtonState,
							});
						}
					})
					.catch(() => {
						setButtonState('active');
						setError('validation took too long');
					});
			} else if (status === 'REDIRECT' && redirectUrl) {
				if (additionalData) {
					sessionStorage.setItem('orderDetail', JSON.stringify(additionalData));
				}
				onRedirect(redirectUrl);
			} else if (status === 'ERROR') {
				setButtonState('active');
				setError(payoneError?.ErrorMessage);
			}
		});
	};

	const payoneAmendPreAuth = (customerHash, creditCardCheckResponse) => {
		const orderHash = sessionStorage.getItem('orderHash');
		const token = sessionStorage.getItem('token');
		const pseudoCardPan = creditCardCheckResponse.pseudocardpan;
		const domain = getUrlRedirectPath();
		const isPreviewEnvironment = process.env.VERCEL_ENV !== 'production';
		const amendPreAuth = {
			orderHash,
			customerHash,
			pseudoCardPan,
			successUrl: `${domain}/processing/updatepayment${setRouteLangParam(lang)}${
				isPreviewEnvironment ? '?featureAppPreview=true' : ''
			}`,
			errorUrl: `${domain}/error${setRouteLangParam(lang)}${
				isPreviewEnvironment ? '?featureAppPreview=true' : ''
			}`,
			backUrl: `${domain}/processing/updatepayment${setRouteLangParam(lang)}${
				isPreviewEnvironment ? '?featureAppPreview=true' : ''
			}`,
		};
		PayOneService.amendPreauthorization(amendPreAuth, token, lang).then((res) => {
			const { offerHash, status, redirectUrl, payoneError } = res;
			if (offerHash) {
				sessionStorage.setItem('offerHash', offerHash);
			}
			if (status === 'APPROVED' && customerHash && orderHash && offerHash) {
				OrderService.amendOrderPayment({ orderHash, offerHash, customerHash }, token).then(
					({ code, orderInfo }) => {
						if (code === 200 && orderInfo) {
							sessionStorage.setItem('orderInfo', JSON.stringify(orderInfo));
							const params = createSearchParams();
							params.set('type', 'updatepayment');
							navigate(getUrlBasePath('/order-confirmation', params));
						}
					},
				);
			} else if (status === 'REDIRECT' && redirectUrl) {
				if (additionalData) {
					sessionStorage.setItem('orderDetail', JSON.stringify(additionalData));
				}
				onRedirect(redirectUrl);
			} else if (status === 'ERROR') {
				setButtonState('active');
				setError(payoneError?.ErrorMessage);
			}
		});
	};

	const validate = () => {
		if (buttonState === 'active') {
			if (window.iframes.isComplete() && !hasErrors() && state.cardHolder.value) {
				setButtonState('loading');
				window.creditCardCheckCallback = (response) => {
					setError(undefined);
					const customerHash = sessionStorage.getItem('customerHash');
					if (response.status === 'VALID' && customerHash) {
						if (paymentUpdate) {
							payoneAmendPreAuth(customerHash, response);
						} else {
							payonePreAuth(customerHash, response);
						}
					} else {
						setButtonState('active');
						setError(response.errormessage);
					}
				};
				window.iframes.creditCardCheck('creditCardCheckCallback');
			} else {
				setError('Bitte alle Felder vollständig ausfüllen');
			}
		}
	};

	const renderCreditCardVerificationInfoFallback = () => (
		<>
			<Typography size="h2" className="py-8" component="p">
				Kartenprüfnummer (CVC Code)
			</Typography>
			<Typography size="xlSpace" className="pb-8" component="p">
				Sie finden den CVC bzw. CVV auf der Rückseite Ihrer Kreditkarte (Mastercard, Visa).
			</Typography>
			<img src={ccBack} alt="cvc help" width="100%" height="100%" />
			<Typography className="pt-2" component="p" color="lightText">
				Sie benötigen die letzten drei Zahlen der Zahlenkombination, die auf der Höhe des Unterschriftfeldes
				gedruckt ist.
			</Typography>
		</>
	);

	useEffect(() => {
		if (showModal && !initialized) {
			if (!window.Payone) {
				const script = document.createElement('script');
				script.src = 'https://secure.pay1.de/client-api/js/v1/payone_hosted_min.js';
				script.async = true;
				script.onload = () => {
					initialize();
				};
				document.head.appendChild(script);
			} else {
				setTimeout(() => {
					initialize();
				}, 100);
			}
		}
	}, [showModal]);

	return (
		<>
			<Modal
				hidden={drawerOpen}
				title={
					<>
						<VisaIcon className="inline mr-1.5" />
						<MastercardIcon className="inline align-middle" />
					</>
				}
				isOpen={showModal}
				onClose={closeModal}>
				{error && renderErrorMessage({ message: error, className: 'mb-5 mx-3' })}
				<div className="p-4">
					{labels?.adacAddress ? (
						<Typography size="lg">
							<Richtext document={labels?.adacAddress} />
						</Typography>
					) : (
						<Typography size="lg">
							ADAC Medien und Reise Gmbh
							<br />
							Hansastrasse 19
							<br />
							80686 München
						</Typography>
					)}
					<div className="mt-4">
						<div className="mb-1.5">
							<Typography component="label" className="block mb-1.5" size="lg" variant="title">
								{labels?.cardholder || 'Karteninhaber'}
							</Typography>
							<Input
								basic
								value={state.cardHolder.value}
								hasError={state.cardHolder.error}
								helperText={
									labels?.errorCardholder || 'Bitte geben Sie den Namen des Kreditkarteninhabers an'
								}
								name="card-holder"
								autocomplete="name"
								className="font-sans"
								onChange={(e) => {
									updateWithValidation('cardHolder', e.target.value, () =>
										checkValidation(e.target.value, 'STRING'),
									);
								}}
								onBlur={(e) =>
									updateWithValidation('cardHolder', e.target.value, () =>
										checkValidation(e.target.value, 'STRING'),
									)
								}
							/>
						</div>
						<div className="mb-1.5">
							<Typography component="label" className="block mb-1.5" size="lg" variant="title">
								{labels?.cardNumber || 'Kartennummer'}
							</Typography>
							<div className="payone__input-wrapper" id="card_pan" placeholder="Kartennummer" />
						</div>
						<div className="mb-1.5">
							<Typography component="label" className="block mb-1.5" size="lg" variant="title">
								{labels?.cardDuration || 'Gültig bis (MM / JJJJ)'}
							</Typography>
							<div className="flex space-x-2" id="card_expire_date">
								<div className="relative" id="card_expire_month" />
								<div className="relative" id="card_expire_year" />
							</div>
						</div>
						<div className="mb-1.5">
							<div className="mb-1.5 flex items-center">
								<Typography component="label" size="lg" variant="title" className="mr-1.5">
									{labels?.cardVerificationNumber || 'Kartenprüfnummer'}
								</Typography>
								<button
									className="cursor-pointer bg-white opacity-80 border-none transition-opacity hover:opacity-100"
									onClick={() => setDrawerOpen(true)}>
									<Info strokeWidth="2" />
								</button>
							</div>
							<div className="payone__input-wrapper" id="card_cvc2" />
						</div>
						<Button fullWidth loading={buttonState === 'loading'} className="mt-3" onClick={validate}>
							{labels?.paymentConfirmation || 'Mit Kreditkarte an ADAC Medien und Reise GmbH zahlen'}
						</Button>
					</div>
				</div>
			</Modal>

			<Drawer
				title={labels?.creditCardVerificationInfo.headline || 'Kartenprüfnummer'}
				paddingX="large"
				isOpen={drawerOpen}
				setIsOpen={setDrawerOpen}>
				{labels?.creditCardVerificationInfo.helpDrawerRichText ? (
					<Richtext document={labels.creditCardVerificationInfo.helpDrawerRichText} />
				) : (
					renderCreditCardVerificationInfoFallback()
				)}
			</Drawer>
		</>
	);
};
